import { useEffect, useState, forwardRef, useRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { 
    TextField, Button, Box, Paper, Checkbox, FormControlLabel, Typography, InputLabel, Alert
} from '@mui/material';
import API from '@utils/axios';
import FormSkeleton from '@components/FormSkeleton';

const CustomerForm = forwardRef(({ initialData, onSubmit, buttons }, ref) => {
    const { t } = useTranslation();
    const [loaded, setLoaded] = useState(false);
    const [logoPreview, setLogoPreview] = useState(null);
    const [duplicateWarning, setDuplicateWarning] = useState(false);
    const fileInputRef = useRef(null);

    const validationSchema = Yup.object({
        name: Yup.string()
            .max(255, t('Must be {{max}} characters or less', { max: 255 }))
            .required(t('This field is required'))
            .test('duplicate', t('A customer with this name already exists'), async (value) => {
                if (!value || value === initialData?.name) return true;
                try {
                    const response = await API.get('/customers', {
                        params: {
                            name: value
                        }
                    });
                    return response.data.data.length === 0;
                } catch (error) {
                    return true;
                }
            }),
        url: Yup.string()
            .url(t('Must be a valid URL'))
            .max(255, t('Must be {{max}} characters or less', { max: 255 }))
            .nullable(),
        logo: Yup.mixed()
            .nullable()
            .test('fileSize', t('File size must be less than 1MB'), (value) => {
                if (!value) return true;
                
                // Si c'est une chaîne base64 existante
                if (typeof value === 'string') {
                    // Extraire la partie base64 après la virgule
                    const base64Data = value.split(',')[1];
                    // Calculer la taille approximative en octets
                    const sizeInBytes = (base64Data.length * 3) / 4;
                    return sizeInBytes <= 1000000;
                }
                
                // Si c'est un fichier uploadé
                if (value instanceof File) {
                    return value.size <= 1000000;
                }
            })
            .test('fileType', t('Only image files are allowed'), (value) => {
                if (!value) return true;
                // Extraire le type MIME de la chaîne base64
                const allowedTypes = [
                    'image/jpeg', 
                    'image/png', 
                    'image/gif',
                    'image/webp',
                    'image/svg+xml',
                    'image/bmp',
                    'image/tiff'
                ];
                if (typeof value === 'string') {
                    const mimeType = value.split(';')[0].split(':')[1];
                    return allowedTypes.includes(mimeType);
                }
                // Si c'est un fichier uploadé
                if (value instanceof File) {
                    return allowedTypes.includes(value.type);
                }
                return false;
            })
    });

    const { control, handleSubmit, setValue, setError, watch, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onChange',
        reValidateMode: 'onBlur',
    });
    useImperativeHandle(ref, () => ({ setError })); // Permettre au composant parent d'appeler setError

    // Watch for name changes to check for duplicates
    const name = watch('name');
    useEffect(() => {
        const checkDuplicate = async () => {
            if (name && name !== initialData?.name) {
                try {
                    const response = await API.get('/customers', {
                        params: {
                            name: name
                        }
                    });
                    setDuplicateWarning(response.data.data.length > 0);
                } catch (error) {
                    setDuplicateWarning(false);
                }
            } else {
                setDuplicateWarning(false);
            }
        };
        checkDuplicate();
    }, [name, initialData?.name]);

    const handleLogoClick = () => {
        fileInputRef.current.click();
    };

    const handleLogoChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setValue('logo', file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleLogoDelete = () => {
        setValue('logo', null);
        setLogoPreview(null);
    };

    useEffect(() => {
        if (initialData) {
            for (const [key, value] of Object.entries(initialData)) {
                setValue(key, value);
            }
            if (initialData.logoUrl) {
                setLogoPreview(initialData.logoUrl);
            }
        }
        setLoaded(true);
    }, [initialData, setValue]);

    const formatSubmit = async (data) => {

        const submitData = { ...data };
            
        if (data.logo instanceof File) {
            // Conversion du fichier en base64
            const base64Logo = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(data.logo);
                reader.onload = () => {
                    resolve(reader.result);
                };
                reader.onerror = error => reject(error);
            });
            
            submitData.logo = base64Logo;
        }

        onSubmit(submitData);
    }

    if (!loaded) return <FormSkeleton nb={2} />;

    return (
        <form onSubmit={handleSubmit(formatSubmit)}>
            <Paper sx={{ p: 2 }}>
                {duplicateWarning && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {t('A customer with this name already exists')}
                    </Alert>
                )}
                <Box mb={1}>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                required
                                fullWidth
                                label={t('Name')}
                                variant="standard"
                                error={Boolean(errors.name)}
                                helperText={errors.name?.message}
                            />
                        )}
                    />
                </Box>

                <Box mb={3}>
                    <Controller
                        name="url"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label={t('Site URL')}
                                variant="standard"
                                error={Boolean(errors.url)}
                                helperText={errors.url?.message}
                            />
                        )}
                    />
                </Box>

                <Box mb={3}>

                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        accept="image/*"
                        onChange={handleLogoChange}
                    />

                    {logoPreview ? (
                        <div>
                            <InputLabel sx={{ mb: 1, color: 'text.primary' }}>{t('Logo')}</InputLabel>
                            <Box
                                component="img"
                                src={logoPreview}
                                sx={{
                                    maxWidth: 300,
                                    maxHeight: 300,
                                    width: 'auto',
                                    height: 'auto',
                                    mb: 1,
                                    objectFit: 'contain'
                                }}
                            />
                            <Box>
                                <Button onClick={handleLogoClick}>{t('Edit')}</Button>
                                <Button onClick={handleLogoDelete}>{t('Delete')}</Button>
                            </Box>
                        </div>
                    ) : (
                        <Button
                            variant="outlined"
                            onClick={handleLogoClick}
                            sx={{ mb: 2 }}
                        >
                            {t('Upload logo')}
                        </Button>
                    )}
                    {errors.logo && (
                        <Typography color="error" variant="caption">
                            {errors.logo.message}
                        </Typography>
                    )}
                </Box>

                <Box>
                    <Controller
                        name="active"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        {...field}
                                        color="primary"
                                        checked={field.value}
                                    />
                                }
                                label={t('Active|customer')}
                            />
                        )}
                    />
                </Box>

            </Paper>
            <Box sx={{ mt: 2 }}>
                <Button type="submit" variant="contained" color="primary">
                    {t('Save')}
                </Button>
                {buttons}
            </Box>
        </form>
    );
});

export default CustomerForm;
